import React, { Suspense, lazy, useContext } from 'react';
import './features/mobileAdmin/adminTheme.css';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthContext } from './context/AuthContext';

import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Users from './pages/Users';
import ProtectedRoute from './components/ProtectedRoute';
import Courses from "./pages/Courses";
import Students from './pages/Students';
import Payments from './pages/Payments';
import StudentDetail from './pages/StudentDetail';
import Teachers from './pages/Teachers';
import StaffPage from './pages/Staff';
import Subcategories from './pages/Subcategories';
import Expenses      from './pages/Expenses';
import SessionLogs from "./pages/SessionLogs";
import TeacherLogs from "./pages/TeacherLogs";
import Settings from './pages/Settings';
import { RoleRoute } from './features/mobileAdmin/components';

const DeviceChangeRequestsPage = lazy(() => import('./features/mobileAdmin/devices/DeviceChangeRequestsPage'));
const CourseDeliveryPage = lazy(() => import('./features/mobileAdmin/delivery/CourseDeliveryPage'));
const OfflineDownloadsPage = lazy(() => import('./features/mobileAdmin/downloads/OfflineDownloadsPage'));
const MaterialCataloguePage = lazy(() => import('./features/mobileAdmin/materials/MaterialCataloguePage'));
const ContentOperationsPage = lazy(() => import('./features/mobileAdmin/content/ContentOperationsPage'));
const OperationsPage = lazy(() => import('./features/mobileAdmin/operations/OperationsPage'));
const MobileAccountSetupPage = lazy(() => import('./features/mobileAdmin/accounts/MobileAccountSetupPage'));
const LiveClassListPage = lazy(() => import('./features/liveClasses/LiveClassListPage'));
const LiveClassForm = lazy(() => import('./features/liveClasses/LiveClassForm'));
const LiveClassDetailPage = lazy(() => import('./features/liveClasses/LiveClassDetailPage'));
const ZoomAccountProfilesPage = lazy(() => import('./features/liveClasses/ZoomAccountProfilesPage'));
const ZoomAccountProfileForm = lazy(() => import('./features/liveClasses/ZoomAccountProfileForm'));
const LiveClassSeriesListPage = lazy(() => import('./features/liveClasses/LiveClassSeriesListPage'));
const LiveClassSeriesDetailPage = lazy(() => import('./features/liveClasses/LiveClassSeriesDetailPage'));
const LiveClassSeriesEditPage = lazy(() => import('./features/liveClasses/LiveClassSeriesEditPage'));
const mobilePage = (page: React.ReactNode) => <Suspense fallback={<main className="ma-route-loading" role="status" aria-live="polite">Loading mobile administration…</main>}>{page}</Suspense>;

function App() {
    const { user, initializing } = useContext(AuthContext);

    return (
        <Routes>
            {/* Public login route */}
            <Route
                path="/"
                element={initializing ? null : user ? <Navigate to="/dashboard" replace /> : <Login />}
            />

            {/* Protected dashboard */}
            <Route
                path="/dashboard"
                element={
                    <ProtectedRoute>
                        <Dashboard />
                    </ProtectedRoute>
                }
            />

            {/* Protected users (only for admin/superadmin, ProtectedRoute then Users.tsx itself will redirect viewer) */}
            <Route
                path="/users"
                element={
                    <ProtectedRoute>
                        <Users />
                    </ProtectedRoute>
                }
            />
            <Route path="/courses" element={<ProtectedRoute><Courses/></ProtectedRoute>} />
            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
            <Route path="/students" element={<ProtectedRoute><Students/></ProtectedRoute>} />
            <Route path="/payments" element={<ProtectedRoute><Payments/></ProtectedRoute>} />
            <Route path="/students/:id" element={<ProtectedRoute><StudentDetail/></ProtectedRoute>} />
            <Route
                path="/teachers"
                element={<ProtectedRoute><Teachers /></ProtectedRoute>}
            />

            <Route
                path="/staff"
                element={<ProtectedRoute><StaffPage /></ProtectedRoute>}
            />
            <Route path="/subcategories" element={
                <ProtectedRoute>
                    <Subcategories />
                </ProtectedRoute>
            }/>
            <Route
                   path="/teachers/:id/logs"
                   element={<ProtectedRoute><TeacherLogs /></ProtectedRoute>}
                 />
            <Route
                    path="/session-logs"
                    element={<ProtectedRoute><SessionLogs /></ProtectedRoute>}
            />
            <Route path="/expenses" element={
                <ProtectedRoute>
                    <Expenses />
                </ProtectedRoute>
            }/>
            <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
            <Route path="/mobile/device-changes" element={<RoleRoute minimum="admin">{mobilePage(<DeviceChangeRequestsPage />)}</RoleRoute>} />
            <Route path="/courses/:courseId/mobile-delivery" element={<RoleRoute minimum="admin">{mobilePage(<CourseDeliveryPage />)}</RoleRoute>} />
            <Route path="/courses/:courseId/offline-downloads" element={<RoleRoute minimum="viewer">{mobilePage(<OfflineDownloadsPage />)}</RoleRoute>} />
            <Route path="/courses/:courseId/materials" element={<RoleRoute minimum="admin">{mobilePage(<MaterialCataloguePage />)}</RoleRoute>} />
            <Route path="/mobile/content" element={<RoleRoute minimum="viewer">{mobilePage(<ContentOperationsPage />)}</RoleRoute>} />
            <Route path="/mobile/account-setup" element={<RoleRoute minimum="admin">{mobilePage(<MobileAccountSetupPage />)}</RoleRoute>} />
            {['accounts','alerts','audit','reports','health'].map(section => <Route key={section} path={`/mobile/${section}`} element={<RoleRoute minimum="viewer">{mobilePage(<OperationsPage />)}</RoleRoute>} />)}
            <Route path="/live-classes" element={<RoleRoute minimum="viewer">{mobilePage(<LiveClassListPage />)}</RoleRoute>} />
            <Route path="/live-classes/new" element={<RoleRoute minimum="admin">{mobilePage(<LiveClassForm />)}</RoleRoute>} />
            <Route path="/live-classes/:id" element={<RoleRoute minimum="viewer">{mobilePage(<LiveClassDetailPage />)}</RoleRoute>} />
            <Route path="/live-classes/:id/edit" element={<RoleRoute minimum="admin">{mobilePage(<LiveClassForm />)}</RoleRoute>} />
            <Route path="/zoom-accounts" element={<RoleRoute minimum="viewer">{mobilePage(<ZoomAccountProfilesPage />)}</RoleRoute>} />
            <Route path="/zoom-accounts/new" element={<RoleRoute minimum="admin">{mobilePage(<ZoomAccountProfileForm />)}</RoleRoute>} />
            <Route path="/zoom-accounts/:id/edit" element={<RoleRoute minimum="admin">{mobilePage(<ZoomAccountProfileForm />)}</RoleRoute>} />
            <Route path="/live-class-series" element={<RoleRoute minimum="viewer">{mobilePage(<LiveClassSeriesListPage />)}</RoleRoute>} />
            <Route path="/live-class-series/:id" element={<RoleRoute minimum="viewer">{mobilePage(<LiveClassSeriesDetailPage />)}</RoleRoute>} />
            <Route path="/live-class-series/:id/edit" element={<RoleRoute minimum="admin">{mobilePage(<LiveClassSeriesEditPage />)}</RoleRoute>} />
        </Routes>
    );
}

export default App;
